<template>
    <div>

        <PageHeader>
            <template v-if="product && product.aggregation">{{ product.aggregation.name }}</template>
            <template #side>
                <ButtonIcon :to="parentRoute">
                    {{ $t('goBack') }}
                </ButtonIcon>
            </template>
        </PageHeader>

        <Card :header="$t('product')" overlay :loading="requestProduct.getLoading(`patch-${id}`) || requestProduct.getLoading() || requestCatalogMapping.getLoading()">

            <ErrorMessage :value="requestProduct.getError()" />


            <div v-if="product" class="grid-template">

                <div class="label">{{ $t('id') }}:</div>
                <div class="value">{{ product.id }}</div>

                <div class="label">{{ $t('status') }}:</div>
                <div class="value">{{ product.status }}</div>

                <div class="label">{{ $t('catalogStatus') }}:</div>
                <div class="value">{{ product.catalogStatus }}</div>

                <div class="label">{{ $t('updatedAt') }}:</div>
                <div class="value">{{ $format.datetime(product.updatedAt) }}</div>

                <div class="label">{{ $t('riskScore') }}:</div>
                <div class="value">{{ product.riskScore }}</div>

                <template v-for="([key, value]) in Object.entries(product.risks)">
                    <div :key="`label-${key}`" class="label ml-4">{{ risks[key] || key }}:</div>
                    <div :key="`value-${key}`" class="value">{{ value }}</div>
                </template>

                <div class="label">{{ $t('aggregation') }}:</div>
                <div class="value"></div>

                <div class="label ml-4">{{ $t('id') }}:</div>
                <div class="value">{{ product.aggregation.id }}</div>

                <div class="label ml-4">{{ $t('offerId') }}:</div>
                <div class="value">{{ product.aggregation.offerId }}</div>

                <div class="label ml-4">{{ $t('name') }}:</div>
                <div class="value">{{ product.aggregation.name }}</div>

                <div class="label ml-4">{{ $t('info') }}:</div>
                <div class="value">{{ product.aggregation.info }}</div>

                <div class="label ml-4 mb-auto">{{ $t('image') }}:</div>
                <div class="value">
                    <div>{{ product.aggregation.image }}</div>
                    <ImageLoader
                        :src="product.aggregation.image"
                        alt=""
                        size="50"
                        image-height="auto"
                        class="mr-2 flex-shrink-0"
                    />
                </div>

            </div>

        </Card>

        <Card :header="$t('catalogSuggestion')" overlay :loading="requestProduct.getLoading(`patch-${id}`) || requestSuggestion.getLoading() || requestCatalogMapping.getLoading()">

            <template #nav v-if="suggestion">
                <div class="d-flex gap-2">
                    <ButtonIcon variant="success" @click="confirmCreate()">
                        {{ $t('createProduct') }}
                    </ButtonIcon>
                    <ButtonIcon variant="warning" @click="confirmIgnore()">
                        {{ $t('ignoreProposal') }}
                    </ButtonIcon>
                    <ButtonIcon variant="primary" @click="confirmEdit()">
                        {{ $t('editProposal') }}
                    </ButtonIcon>
                </div>
            </template>

            <template v-if="suggestion">

                <ErrorMessage :value="requestSuggestion.getError()" />

                <b-table-simple small borderless>
                    <b-thead class="thead-dark">
                        <b-tr class="border-bottom">
                            <b-th>{{ $t('name') }}</b-th>
                            <b-th>{{ $t('author') }}</b-th>
                            <b-th>{{ $t('brand') }}</b-th>
                            <b-th>{{ $t('description') }}</b-th>
                            <b-th>{{ $t('source') }}</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr>
                            <b-td>{{ suggestion.name }}</b-td>
                            <b-td>{{ suggestion.author }}</b-td>
                            <b-td>{{ suggestion.brand }}</b-td>
                            <b-td>{{ suggestion.description }}</b-td>
                            <b-td>{{ suggestion.source }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <div class="my-3 d-table"></div>

                <h6>{{ $t('variants') }}</h6>
                <b-table-lite
                    :items="variants"
                    :fields="variantsFields"
                    small
                    responsive
                    thead-tr-class="border-bottom"
                    head-variant="secondary"
                    details-td-class="border-bottom border-light"
                >
                    <template #head()="scope">{{ $tx(camelCase(scope.label), scope.label) }}</template>
                    <template #row-details="{item}">
                        <b-table-simple hover small responsive borderless class="m-0">
                            <b-thead>
                                <b-tr>
                                    <b-th>
                                        <div class="d-flex gap-2 align-items-center accordion">
                                            <ButtonIcon
                                                v-b-toggle="`accordion-${item.offers.join('-')}`"
                                                variant="secondary"
                                                icon="chevron-down"
                                                flip-icon
                                                size="sm"
                                                class="expandable p-1"
                                                :disabled="!!getOffersLoading(item.offers).length"
                                            />
                                            <span>{{ $t('offers') }}:</span>
                                            <span>
                                                <span v-if="!!getOffersLoading(item.offers).length" class="font-weight-normal">{{ item.offers.length - getOffersLoading(item.offers).length }}/</span>
                                                <span v-else-if="!!getOffersError(item.offers).length" class="font-weight-normal">{{ item.offers.length - getOffersError(item.offers).length }}/</span>
                                                <span class="font-weight-normal">{{ item.offers.length }}</span>
                                            </span>
                                            <b-spinner v-if="!!getOffersLoading(item.offers).length" small />
                                            <Tooltip
                                                v-else-if="!!getOffersError(item.offers).length"
                                                type="alert-circle"
                                                :size="20"
                                                variant="danger"
                                                icon-variant="danger"
                                                :html="getOffersErrorTooltipHtml(item.offers)"
                                            />
                                        </div>
                                    </b-th>
                                </b-tr>
                            </b-thead>
                        </b-table-simple>
                        <b-collapse :id="`accordion-${item.offers.join('-')}`" visible>
                            <b-table-lite
                                v-if="!getOffersLoading(item.offers).length"
                                :items="item.offers.map(offerId => offers[offerId]).filter(offer => !!offer)"
                                :fields="offersFields"
                                small
                                borderless
                                responsive
                                thead-tr-class="border-bottom"
                            >
                                <template #cell(product)="data">
                                    <div class="d-flex flex-column gap-2">
                                        <b-link v-if="data.item.product" :to="{ name: 'Offers', query: { product: data.item.product } }">
                                            {{ data.item.product }}
                                        </b-link>
                                        <div class="info text-secondary font-size-12">
                                            {{ data.item.offerId }}
                                        </div>
                                    </div>
                                </template>

                                <template #cell(ean)="data">
                                    <b-link :to="{ name: 'Offers', query: { ean: data.item.ean } }">
                                        {{ data.item.ean }}
                                    </b-link>
                                </template>

                                <template #cell(name)="data">
                                    <div class="d-flex">
                                        <ImageLoader
                                            :src="data.item.image"
                                            alt=""
                                            size="50"
                                            image-height="auto"
                                            class="mr-2 flex-shrink-0"
                                        />
                                        <div class="d-flex flex-column gap-2">
                                            <b-link class="title" :to="createItemUrl(data.item)">
                                                {{ data.item.name }}
                                            </b-link>
                                            <div class="info text-secondary font-size-12">
                                                {{ data.item.categories.join(',') }}
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <template #cell(feed)="data">
                                    <div class="d-flex flex-column gap-2">
                                        {{ data.item.feed }}
                                        <div v-if="data.item.campaign" class="info">
                                            {{ data.item.campaign.name }}
                                        </div>
                                    </div>
                                </template>

                                <template #cell(price)="data">
                                    <div class="nowrap">
                                        {{ $format.currency(data.item.price) }}
                                    </div>
                                </template>

                                <template #cell(type)="data">
                                    {{ getTypeLabel(data.item.type) }}<br>
                                    <template v-if="data.item.format">{{ data.item.format }}</template>
                                </template>

                                <template #cell(hidden)="data">
                                    <BadgeBoolean :value="data.item.hidden" true-value-badge-variant="danger" false-value-badge-variant="success" />
                                </template>

                                <template #cell(active)="data">
                                    <div class="d-flex flex-column gap-2">
                                        <div><BadgeBoolean :value="data.item.active" :true-value-text="$t('available2')" :false-value-text="$t('unavailable2')" /></div>
                                        <div class="info text-secondary font-size-12">
                                            {{ $format.dateAndHour(data.item.lastAvailabilityChangedAt, 'yyyy-MM-dd') }}
                                        </div>
                                    </div>
                                </template>
                            </b-table-lite>
                        </b-collapse>
                    </template>
                </b-table-lite>

                <ModalEdit />

            </template>

        </Card>


    </div>
</template>



<script>
    import { CATALOG_STATUS } from '@/services/productService';
    import camelCase from 'camelcase';
    import ImageLoader from '@/components/common/ImageLoader.vue';
    import BadgeBoolean from '@/components/common/BadgeBoolean.vue';
    import Tooltip from '@/components/common/Tooltip.vue';
    import ModalEdit from './parts/ModalEdit.vue';

    export default {
        name: 'AggregateModerationDetails',
        components: {
            ImageLoader,
            BadgeBoolean,
            Tooltip,
            ModalEdit,
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                id: Number.parseInt(this.$route.params.id),
                requestProduct: this.$req('product/products'),
                requestSuggestion: this.$req('product/catalogSuggestion'),
                requestOffer: this.$req('offers/offer'),
                requestTypesAndFormats: this.$req('offers/typesAndFormats'),
                requestCatalogMapping: this.$req('product/catalogMapping'),
                requestRiskDimensions: this.$req('product/RiskDimensions'),
            };
        },
        computed: {
            parentRoute() {
                return this.$route.params.parentUrl ? this.$router.resolve(this.$route.params.parentUrl).resolved : this.$route.matched.slice(-2)[0];
            },
            product() {
                return this.requestProduct.getData();
            },
            suggestion() {
                return this.requestSuggestion.getData();
            },
            variants() {
                return this.suggestion?.variants?.map(item => ({ ...item, _showDetails: true })) || [];
            },
            variantsFields() {
                return Object.keys(this.suggestion?.variants[0] || {}).filter(item => item !== 'offers');
            },
            offersFields() {
                return [
                    { key: 'product', label: this.$t('productOfferId') },
                    { key: 'ean', label: this.$t('ean') },
                    { key: 'name', label: this.$t('name') },
                    { key: 'info', label: this.$t('info') },
                    { key: 'code', label: this.$t('code') },
                    { key: 'feed', label: this.$t('feedShop') },
                    { key: 'type', label: this.$t('offerType') },
                    { key: 'price', label: this.$t('price') },
                    { key: 'hidden', label: this.$t('hidden') },
                    { key: 'active', label: this.$t('availability') },
                ];
            },
            offerIds() {
                return this.variants?.map(variant => variant.offers).flat(10);
            },
            offers() {
                return this.offerIds.reduce((acc, offerId) => ({ ...acc, [offerId]: this.requestOffer.getData(offerId) }), {});
            },
            typesAndFormats() {
                return this.requestTypesAndFormats.getData() || [];
            },
            riskDimensions() {
                return this.requestRiskDimensions.getData() || [];
            },
            risks() {
                return this.riskDimensions.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {});
            },
        },
        watch: {
            offerIds: {
                handler: 'fetchOffers',
            },
        },
        methods: {
            camelCase,
            fetchProduct() {
                return this.requestProduct.get({ urlParams: { id: this.id } }).catch(() => {});
            },
            fetchSuggestion() {
                return this.requestSuggestion.get({ urlParams: { id: this.id } }).catch(() => {});
            },
            fetchOffers() {
                this.offerIds.forEach(offerId => {
                    this.requestOffer
                        .id(offerId)
                        .get({ urlParams: { offerId } })
                        .catch(() => {});
                });
            },
            getOffersLoading(offerIds) {
                return offerIds.map(offerId => this.requestOffer.getLoading(offerId)).filter(loading => !!loading);
            },
            getOffersError(offerIds) {
                return offerIds.map(offerId => this.requestOffer.getError(offerId)).filter(error => !!error);
            },
            createItemUrl(item) {
                return {
                    name: 'OffersDetails',
                    params: { id: item.id, parentUrl: this.$route.fullPath },
                    query: {},
                };
            },
            getTypeLabel(type) {
                return this.typesAndFormats.find(item => item.value === type)?.label || type;
            },
            fetchTypesAndFormats() {
                return this.requestTypesAndFormats.get().catch(() => {});
            },
            getOffersErrorTooltipHtml(offersIds) {
                const errorHeader = `<div><strong>${this.$t('errorOccurred')}:</strong></div>`;
                const errorMessages = offersIds.reduce((acc, offerId) => (this.requestOffer.getError(offerId) ? `${acc}<div>${offerId}: ${this.requestOffer.getError(offerId).message}</div>` : acc), '');
                return `<div class="text-left">${errorHeader}${errorMessages}</div>`;
            },
            confirmCreate() {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmCreationAProductFromSuggestion'), {
                        title: this.$t('createProduct'),
                        okTitle: this.$t('create'),
                        cancelTitle: this.$t('cancel'),
                        centered: true,
                    })
                    .then(value => {
                        if (value) {
                            this.requestCatalogMapping
                                .post({ urlParams: { id: this.id }, requestParams: this.suggestion })
                                .then(data => {
                                    this.$toastr.success(this.$t('dataSavedSuccessfully'));
                                    this.$router.push(this.parentRoute);
                                })
                                .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
                        }
                    });
            },
            confirmIgnore() {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmIgnoringSuggestion'), {
                        title: this.$t('ignoreSuggestion'),
                        okTitle: this.$t('ignore'),
                        cancelTitle: this.$t('cancel'),
                        centered: true,
                    })
                    .then(value => {
                        if (value) {
                            this.requestProduct
                                .id(`patch-${this.id}`)
                                .patch({ urlParams: { id: this.id }, requestParams: { catalogStatus: CATALOG_STATUS.REJECTED } })
                                .then(data => {
                                    this.$toastr.success(this.$t('proposalHasBeenSuccessfullyIgnored'));
                                    this.$router.push(this.parentRoute);
                                })
                                .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
                        }
                    });
            },
            confirmEdit() {
                this.$root.$emit('bv::show::modal', 'modal-aggregate-moderation-product-edit');
            },
            fetchRiskDimensions() {
                return this.requestRiskDimensions.get();
            },
        },
        created() {
            this.fetchProduct();
            this.fetchSuggestion();
            this.fetchRiskDimensions();
        },
    };
</script>



<style lang="scss" scoped>
    .grid-template {
        display: grid;
        grid-template-columns: minmax(60px, max-content) auto;
        column-gap: 1rem;
        row-gap: 0.4rem;
        align-items: center;

        .label {
            font-weight: bold;
        }
    }
</style>
